import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ChatInput from './ChatInput/ChatInput';
import * as Styled from './VHTextChatInput.styled';
import DeepgramButton from './DeepgramButton/DeepgramButton';
import DeepgramButtonPushToTalk from '@vh/components/VHChatInputArea/VHTextChatInput/DeepgramButtonPushToTalk/DeepgramButtonPushToTalk';
import MicBehaviorSelector from '@vh/components/VHChatInputArea/MicBehaviorSelector/MicBehaviorSelector';
import { MicBehaviorContext } from '@vh/components/VHChatInputArea/MicBehaviorProvider.context';
import DeepgramButtonRecordAndSend from '@vh/components/VHChatInputArea/VHTextChatInput/DeepgramButtonRecordAndSend/DeepgramButtonRecordAndSend';
import { MicMutedContext } from '@vh/components/VHChatInputArea/MicMutedProvider.context';
import { useTranslation } from 'react-i18next';

/**
 * @type {import('react').FC<{
    onSubmit: (chat: string) => void;
    onChange: (chat: string) => void;
    chat: string;
    appendPreviewChat: string;
    textDisabled: boolean;
    inputDisabledText: string;
    submitDisabled: boolean;
    recordingDisabled: boolean;
    maxLength: number;
    onPendingTranscription: (pending: boolean) => void
    onPreviewChatChange: (newVal: string) => void;
* }>}
*/
const VHTextChatInput = ({
  onSubmit,
  onChange,
  chat,
  appendPreviewChat,
  textDisabled,
  inputDisabledText,
  submitDisabled,
  recordingDisabled,
  maxLength,
  onPendingTranscription,
  onPreviewChatChange,
}) => {
  const chatInputRef = useRef(null);
  const [speechError, setSpeechError] = useState(null);
  const { setMuted } = useContext(MicMutedContext);

  const handleChatChange = useCallback(
    (e) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  // scroll chat input if not focused
  useEffect(() => {
    const isFocused = document.activeElement === chatInputRef.current;
    if (chatInputRef.current && !isFocused) {
      chatInputRef.current.scrollLeft =
        chatInputRef.current.scrollWidth - chatInputRef.current.offsetWidth;
      chatInputRef.current.scrollTop =
        chatInputRef.current.scrollHeight - chatInputRef.current.offsetHeight;
    }
  }, [chat, appendPreviewChat]);

  // Focus the input when it becomes available
  const editable = !textDisabled && !recordingDisabled;
  useEffect(() => {
    if (editable && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [editable]);

  const handleOnSpeechError = useCallback((err) => {
    setPTTRecording(false);
    setSpeechError(err);
  }, []);
  const { micBehavior } = useContext(MicBehaviorContext);
  const [rasRecording, setRASRecording] = useState(false);
  const [pttRecording, setPTTRecording] = useState(false);
  const handlePTTRecording = useCallback((newRecording) => {
    setPTTRecording(newRecording);
  }, []);

  const MicButtonComponent = useMemo(() => {
    if (micBehavior === 'open') {
      return (
        <DeepgramButton
          onChange={onChange}
          recordingDisabled={recordingDisabled}
          textDisabled={textDisabled}
          onSubmit={onSubmit}
          onSpeechError={handleOnSpeechError}
          design="transparent"
        />
      );
    } else if (micBehavior === 'record_and_send') {
      return (
        <DeepgramButtonRecordAndSend
          onChange={onChange}
          recordingDisabled={recordingDisabled}
          textDisabled={textDisabled}
          onSubmit={onSubmit}
          onSpeechError={handleOnSpeechError}
          currentChat={chat}
          onPendingTranscription={onPendingTranscription}
          design="transparent"
          recording={rasRecording}
          setRecording={setRASRecording}
        />
      );
    }
    return (
      <DeepgramButtonPushToTalk
        onChange={onChange}
        recordingDisabled={recordingDisabled}
        textDisabled={textDisabled}
        onSubmit={onSubmit}
        onSpeechError={handleOnSpeechError}
        currentChat={chat}
        onPendingTranscription={onPendingTranscription}
        onPreviewChatChange={onPreviewChatChange}
        design="transparent"
        onRecording={handlePTTRecording}
        recording={pttRecording}
        appendPreviewChat={appendPreviewChat}
      />
    );
  }, [
    micBehavior,
    onChange,
    recordingDisabled,
    textDisabled,
    onSubmit,
    handleOnSpeechError,
    chat,
    onPendingTranscription,
    onPreviewChatChange,
    handlePTTRecording,
    pttRecording,
    appendPreviewChat,
    rasRecording,
  ]);

  // ensure correct spacing based on `chat` and `appendPreviewChat` vals
  const chatInputValue = [chat, appendPreviewChat].filter(Boolean).join(' ');
  // could maybe be moved to a push to talk context
  const handleChatInputFocus = useCallback(() => {
    if (micBehavior === 'push_to_talk') {
      setMuted(true);
      setPTTRecording(false);
    }
  }, [setMuted, micBehavior]);

  const { t } = useTranslation();

  let placeholderText = t('typeHereOrPressMic');
  if (pttRecording || rasRecording) {
    placeholderText = t('recordingVoice');
  } else if (textDisabled) {
    placeholderText = inputDisabledText;
  } else if (recordingDisabled) {
    placeholderText = '';
  }

  return (
    <>
      <Styled.ChatInputWrapper>
        <ChatInput
          onFocus={handleChatInputFocus}
          value={chatInputValue}
          disabled={textDisabled || recordingDisabled}
          onChange={handleChatChange}
          placeholder={placeholderText}
          ref={chatInputRef}
          onEnter={() => {
            if (submitDisabled) return;
            setSpeechError(null);
            onSubmit(chat);
          }}
          maxLength={maxLength}
        />
        <Styled.ExtraWrapper>
          {MicButtonComponent}
          <MicBehaviorSelector
            disabled={recordingDisabled || textDisabled}
            solidButton={false}
          />
        </Styled.ExtraWrapper>
      </Styled.ChatInputWrapper>
    </>
  );
};

export default VHTextChatInput;
